import { useCallback, useEffect, useMemo, useState, type RefObject } from 'react'
import type { SWRInfiniteResponse } from 'swr/infinite'
import type { ImageSearchHit, ImageSearchResult } from '../lib/types'
import {
  countGridColumns,
  flattenHits,
  getAdjacentHit,
  getLastHitBelow,
  isReachingEnd
} from '../lib/previewNavigation'

export interface PreviewNavigation {
  /** All loaded hits, deduped across pages */
  hits: ImageSearchHit[]
  hit: ImageSearchHit | undefined
  /** The previewed hit, or the last one previewed after the dialog closed */
  highlightedId: string | null
  hasPrev: boolean
  hasNext: boolean
  isLoadingNext: boolean
  open: (hit: ImageSearchHit) => void
  close: () => void
  prev: () => void
  next: () => void
  /** Steps a grid row back */
  up: () => void
  /** Steps a grid row forward, or onto a shorter last row once the results have ended */
  down: () => void
  clearHighlight: () => void
}

/** Pages to fetch past the requested one when they only repeat loaded hits */
const MAX_EXTRA_PAGES = 1

interface PendingNext {
  /** Hits to step forward once the page has arrived */
  step: number
  /** Page count to wait for before giving up or fetching an extra page */
  size: number
  extraPages: number
}

/**
 * Tracks which search hit is previewed and steps through all loaded hits, one at a time or
 * a grid row at a time, fetching the next page when stepping past the last loaded one.
 */
export const usePreviewNavigation = (
  swr: SWRInfiniteResponse<ImageSearchResult, Error>,
  pageSize: number,
  gridRef: RefObject<HTMLElement | null>
): PreviewNavigation => {
  const { data, error, isValidating, size, setSize } = swr
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [highlightedId, setHighlightedId] = useState<string | null>(null)
  const [pendingNext, setPendingNext] = useState<PendingNext | null>(null)

  const hits = useMemo(() => flattenHits(data), [data])
  const ending = isReachingEnd(data, pageSize)
  const index = hits.findIndex((hit) => hit.id === selectedId)
  const hit = index === -1 ? undefined : hits[index]
  const isHighlightInHits = hits.some((hit) => hit.id === highlightedId)

  const select = useCallback((id: string) => {
    setSelectedId(id)
    setHighlightedId(id)
  }, [])

  // A forward step past the end lands on a shorter last row, never along the current one
  const selectLastHitBelow = useCallback((step: number) => {
    const lastHit = getLastHitBelow(hits, selectedId, step)

    if (lastHit) {
      select(lastHit.id)
    }
  }, [hits, selectedId, select])

  // Drop a selection that left the results, e.g. after a new search
  useEffect(() => {
    if (selectedId !== null && index === -1) {
      setSelectedId(null)
      setPendingNext(null)
    }
  }, [selectedId, index])

  useEffect(() => {
    if (highlightedId !== null && !isHighlightInHits) {
      setHighlightedId(null)
    }
  }, [highlightedId, isHighlightInHits])

  // Finish a step forward once the requested page has arrived, or give up
  useEffect(() => {
    if (!pendingNext) {
      return
    }

    const nextHit = getAdjacentHit(hits, selectedId, pendingNext.step)

    if (nextHit) {
      select(nextHit.id)
      setPendingNext(null)
      return
    }

    if (isValidating) {
      return
    }

    if (error) {
      setPendingNext(null)
      return
    }

    if (ending) {
      selectLastHitBelow(pendingNext.step)
      setPendingNext(null)
      return
    }

    // The page arrived but only repeated loaded hits, as offsets shift when images are published
    if ((data?.length ?? 0) >= pendingNext.size) {
      if (pendingNext.extraPages >= MAX_EXTRA_PAGES) {
        setPendingNext(null)
        return
      }

      setPendingNext({
        ...pendingNext,
        size: pendingNext.size + 1,
        extraPages: pendingNext.extraPages + 1
      })
      void setSize(pendingNext.size + 1)
    }
  }, [
    pendingNext,
    hits,
    data,
    selectedId,
    isValidating,
    ending,
    error,
    select,
    selectLastHitBelow,
    setSize
  ])

  const open = useCallback((hit: ImageSearchHit) => {
    select(hit.id)
    setPendingNext(null)
  }, [select])

  const close = useCallback(() => {
    setSelectedId(null)
    setPendingNext(null)
  }, [])

  const clearHighlight = useCallback(() => {
    setHighlightedId(null)
  }, [])

  const stepBack = useCallback((step: number) => {
    const prevHit = getAdjacentHit(hits, selectedId, -step)

    if (prevHit) {
      select(prevHit.id)
      setPendingNext(null)
    }
  }, [hits, selectedId, select])

  const stepForward = useCallback((step: number) => {
    if (pendingNext || index === -1) {
      return
    }

    const nextHit = getAdjacentHit(hits, selectedId, step)

    if (nextHit) {
      select(nextHit.id)
      return
    }

    if (ending) {
      selectLastHitBelow(step)
      return
    }

    setPendingNext({ step, size: size + 1, extraPages: 0 })
    void setSize(size + 1)
  }, [pendingNext, index, hits, selectedId, ending, size, setSize, select, selectLastHitBelow])

  const prev = useCallback(() => {
    stepBack(1)
  }, [stepBack])

  const next = useCallback(() => {
    stepForward(1)
  }, [stepForward])

  // Columns are read on each press, as they change with the view's width
  const up = useCallback(() => {
    stepBack(countGridColumns(gridRef.current))
  }, [stepBack, gridRef])

  const down = useCallback(() => {
    stepForward(countGridColumns(gridRef.current))
  }, [stepForward, gridRef])

  return {
    hits,
    hit,
    highlightedId,
    hasPrev: index > 0,
    hasNext: index !== -1 && (index < hits.length - 1 || !ending),
    isLoadingNext: pendingNext !== null,
    open,
    close,
    prev,
    next,
    up,
    down,
    clearHighlight
  }
}
